import { Component, HostListener, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { AiKnowledgeService, ApplicationKnowledgeComponent } from '@galaxy/ai-knowledge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { KnowledgeSource } from '@vendasta/embeddings';
import { StickyFooterComponent } from '../../../components/sticky-footer/sticky-footer.component';
import { SubscriptionSelectorComponent } from '../subscription-selector/subscription-selector.component';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PARTNER_ID_TOKEN } from '../../../../../core/src/lib/tokens';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'inbox-platform-assistant-settings',
  templateUrl: './platform-assistant-settings.component.html',
  styleUrl: './platform-assistant-settings.component.scss',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    TranslateModule,
    ApplicationKnowledgeComponent,
    GalaxyFormFieldModule,
    MatCheckboxModule,
    StickyFooterComponent,
    SubscriptionSelectorComponent,
    GalaxyPageModule,
  ],
})
export class PlatformAssistantSettingsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  private readonly knowledgeAppType = 'PlatformChatAssistant';

  protected readonly knowledgeInfo = toSignal(
    this.partnerId$.pipe(
      map((partnerId: string) => ({
        //TODO: MEGA-1201 get the correct knowledge Information
        knowledgeAppId: 'VMF-AI-ASSISTANT',
        knowledgeAppName: 'Platform Chat Assistant',
        knowledgeAppConfigPath: `/ai-assistant/settings?partnerId=${partnerId}`,
      })),
    ),
    { initialValue: { knowledgeAppId: '', knowledgeAppName: '', knowledgeAppConfigPath: '' } },
  );

  readonly isLoading = signal(false);

  readonly selectedSources = signal<KnowledgeSource[]>(null);

  private hasUnsavedChanges = false;
  private initialSources: KnowledgeSource[] | null = null;

  readonly selectedSubscriptionLevels = signal<string[]>([]);

  constructor() {
    effect(() => {
      this.selectedSources();
      this.markChanges();
    });
    this.getSelectedSubscriptionLevels();
  }

  private knowledgeSourcesHaveChanged(): boolean {
    const currentSources: KnowledgeSource[] = this.selectedSources();
    if (this.initialSources?.length !== currentSources?.length) {
      return true;
    }
    for (const source of this.initialSources || []) {
      if (!currentSources.some((s) => s.id === source.id)) {
        return true;
      }
    }
    return false;
  }

  updateSelectedSources(sources: KnowledgeSource[] | null): void {
    if (this.initialSources === null && sources !== null) {
      this.initialSources = structuredClone(sources);
    }
    this.selectedSources.set(sources);
  }

  getSelectedSubscriptionLevels() {
    //TODO: MEGA-1201 get the correct subscription levels if applies
    const hardCodedValues = ['Free', 'Starter', 'Professional', 'Premium', 'Enterprise'];
    this.selectedSubscriptionLevels.set(hardCodedValues);
    return;
  }

  setSubscriptionLevels(selectedSubscriptionLevels: string[]): void {
    this.selectedSubscriptionLevels.set(selectedSubscriptionLevels);
  }

  @HostListener('window: beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
      event.stopPropagation();
      event.returnValue = false;
    }
  }

  private markChanges(): void {
    if (this.knowledgeSourcesHaveChanged()) {
      this.hasUnsavedChanges = true;
      return;
    }
    this.hasUnsavedChanges = false;
  }

  markAsSaved() {
    this.initialSources = structuredClone(this.selectedSources());
    this.hasUnsavedChanges = false;
  }

  async save(): Promise<void> {
    if (!this.hasUnsavedChanges) {
      this.snackbarService.openErrorSnack('No changes made');
      return;
    }

    try {
      //TODO: MEGA-1201 uncomment this code when we got the correct knowledge info
      // const knowledgeInfo = this.knowledgeInfo();
      // await this.knowledgeService.upsertKnowledgeForApp(
      //   knowledgeInfo.knowledgeAppId,
      //   knowledgeInfo.knowledgeAppName,
      //   knowledgeInfo.knowledgeAppConfigPath,
      //   this.knowledgeAppType,
      //   this.selectedSources(),
      // );
      this.markAsSaved();
      this.snackbarService.openSuccessSnack('AI assistant settings saved');
    } catch (e) {
      this.snackbarService.openErrorSnack('AI assistant settings could not be saved');
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async back(): Promise<void> {
    await this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }
}
